import {
  type PartialDate,
  parsePartialDate,
} from "@/lib/tool-kit/citation-parts";
import { mlaPageRange } from "@/lib/tool-kit/legal-sources/common";
import { splitPages } from "@/lib/tool-kit/special-sources/common";

/*
 * Small pieces the citation-sources-books generators share.
 */

const RE_RANGE_MARK = /[-–—,]/;
const RE_ONLY_DIGITS = /^\d+$/;
const RE_EN_DASH = /–/g;
const RE_DASHES = /\s*[-–—]\s*/;

/** "p. 321" for one page, "pp. 321–24" for a range or a list — APA's locator. */
export function apaPageLocator(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") {
    return "";
  }
  const [from, to] = splitPages(trimmed);
  if (to) {
    return `pp. ${from}–${to}`;
  }
  return RE_RANGE_MARK.test(trimmed)
    ? `pp. ${trimmed.replace(RE_DASHES, "–")}`
    : `p. ${trimmed}`;
}

/** A page or range typed in any dash, with an en dash between the numbers. */
export function enDashRange(text: string): string {
  return text.trim().replace(RE_DASHES, "–");
}

/** The same range with MLA's hyphen (MLA writes "321-24"). */
export function hyphenRange(text: string): string {
  return enDashRange(text).replace(RE_EN_DASH, "-");
}

/** True for a plain whole number typed into a field. */
export function isWholeNumber(text: string): boolean {
  return RE_ONLY_DIGITS.test(text.trim());
}

/** A year (or fuller date) field; null when blank or malformed. */
export function readDate(text: string): PartialDate | null {
  return parsePartialDate(text.trim());
}

/** The year as a string, or "n.d." */
export function yearText(date: PartialDate | null): string {
  return date ? String(date.year) : "n.d.";
}

const RE_VOLUME_WORD = /^vol(?:ume)?s?\.?\s*/i;
const RE_ROMAN_VOLUME = /^[IVX]+$/;
const ROMAN_DIGITS: Record<string, number> = { I: 1, V: 5, X: 10 };

/**
 * A volume number as the three styles print it: Arabic. "III" → "3" (CMOS and the MLA
 * Handbook both convert a Roman volume number); a letter such as "C" or "2A" stays as
 * typed, since volumes are sometimes lettered.
 */
export function arabicVolume(text: string): string {
  const trimmed = text.trim().replace(RE_VOLUME_WORD, "");
  if (!RE_ROMAN_VOLUME.test(trimmed)) {
    return trimmed;
  }
  let total = 0;
  for (let index = 0; index < trimmed.length; index++) {
    const value = ROMAN_DIGITS[trimmed[index]];
    const next = ROMAN_DIGITS[trimmed[index + 1] ?? ""] ?? 0;
    total += value < next ? -value : value;
  }
  return String(total);
}

const RE_PAGE_SPAN = /^\s*([\p{L}\d]+)\s*(?:[-–—]|to)\s*([\p{L}\d]+)\s*$/u;

/**
 * "343-47", "xi–xxxvii" or "33 to 54" → [from, to]; one page → [page, ""]. Unlike
 * `splitPages` in the special-sources kit it also reads Roman-numeral front-matter
 * pages, which is where a foreword lives.
 */
export function pageEnds(text: string): [string, string] {
  const match = RE_PAGE_SPAN.exec(text);
  if (match) {
    return [match[1], match[2]];
  }
  return [text.trim(), ""];
}

/** MLA's locator, "p. 35" or "pp. 33-54": numbers shortened as MLA does, hyphen kept. */
export function mlaPageLocator(text: string): string {
  const [from, to] = pageEnds(text);
  if (from === "") {
    return "";
  }
  if (to === "") {
    return `p. ${from}`;
  }
  const bothNumbers = RE_ONLY_DIGITS.test(from) && RE_ONLY_DIGITS.test(to);
  const range = bothNumbers ? mlaPageRange(from, to) : `${from}–${to}`;
  return `pp. ${range.replace(RE_EN_DASH, "-")}`;
}
